import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { ChevronLeft, ChevronRight, ExternalLink, CalendarDays, DollarSign } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW1 = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Fixed category colours (spec). Existing backend event sources are MAPPED to
// these categories without changing any records or scheduling logic.
export const CATS = {
  payroll:          { color: '#2457B3', text: '#FFFFFF', name: 'Payroll & pay dates' },
  inspection:       { color: '#7043A2', text: '#FFFFFF', name: 'Inspections' },
  corrective:       { color: '#A64B16', text: '#FFFFFF', name: 'Corrective actions' },
  supply_order:     { color: '#287A43', text: '#FFFFFF', name: 'Supply orders' },
  vehicle:          { color: '#405B78', text: '#FFFFFF', name: 'Vehicle inspections' },
  special_wo:       { color: '#20242A', text: '#FFFFFF', name: 'Special work orders' },
  contract_startup: { color: '#B52F36', text: '#FFFFFF', name: 'Contract startups' },
  client_visit:     { color: '#F3C846', text: '#20242A', name: 'Client visits & walkthroughs' },
  communication:    { color: '#087C83', text: '#FFFFFF', name: 'Required communications & follow-ups' },
  agreement:        { color: '#9A3412', text: '#FFFFFF', name: 'Service agreements & deadlines' },
  birthday:         { color: '#D6336C', text: '#FFFFFF', name: 'Personnel birthdays' },
  company:          { color: '#0E7490', text: '#FFFFFF', name: 'Company events' },
};
// Legend order follows the spec's listing.
const LEGEND_ORDER = ['payroll', 'inspection', 'corrective', 'supply_order', 'vehicle', 'special_wo', 'contract_startup', 'client_visit', 'communication', 'agreement', 'birthday', 'company'];
// Backend event source -> fixed category.
export const SRC_TO_CAT = {
  payroll: 'payroll',
  inspection: 'inspection',
  corrective_action: 'corrective',
  vehicle_inspection: 'vehicle',
  work_order: 'special_wo',
  follow_up: 'communication',
  incident: 'communication',
  supply_order: 'supply_order',
  contract_startup: 'contract_startup',
  client_visit: 'client_visit',
  contract_expiration: 'agreement',
  contract_notice: 'agreement',
  birthday: 'birthday',
  company_event: 'company',
};
export const catOf = (source) => CATS[SRC_TO_CAT[source]] || { color: '#475569', text: '#FFFFFF', name: 'Other' };

const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;              // m is 0-based
const mmddyy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${m}/${d}/${y.slice(2)}`; };
const etTodayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

// Side months = exactly two months BEFORE and two months AFTER the displayed
// month, chronological (across year boundaries). Returns 5 ordered entries
// (incl. the displayed month); the mini list excludes the displayed month.
const monthWindow = (year, month) => {
  const out = [];
  for (let off = -2; off <= 2; off++) {
    let y = year, m = month + off;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    out.push({ year: y, month: m });
  }
  return out;
};

// Cells for a month grid: leading blanks + numbered days, padded to full weeks.
const monthCells = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const CAP = 3; // event rows shown per date before "+N more"

const WelcomeCalendar = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const today = etTodayISO();
  const [sel, setSel] = useState(() => { const [y, m] = today.split('-'); return { year: +y, month: +m - 1 }; });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailDate, setDetailDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const window6 = useMemo(() => monthWindow(sel.year, sel.month), [sel]);
  const rangeStart = iso(window6[0].year, window6[0].month, 1);
  const last = window6[window6.length - 1];
  const rangeEnd = iso(last.year, last.month, new Date(last.year, last.month + 1, 0).getDate());

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calendar/events`, {
        params: { start: rangeStart, end: rangeEnd },
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(r.data.events || []);
    } catch (e) {
      setEvents([]);
    } finally { setLoading(false); }
  }, [token, rangeStart, rangeEnd]);

  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => {
    const m = {};
    for (const e of events) { (m[e.date] = m[e.date] || []).push(e); }
    return m;
  }, [events]);

  const grid = useMemo(() => monthCells(sel.year, sel.month), [sel]);

  const stepMonth = (delta) => {
    let y = sel.year, m = sel.month + delta;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    setSel({ year: y, month: m });
  };

  const openDay = (ds) => { setSelectedDate(ds); setDetailDate(ds); };
  const detailItems = detailDate ? (byDate[detailDate] || []) : [];
  const minis = window6.filter((w) => !(w.year === sel.year && w.month === sel.month));

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm sm:p-5" data-testid="welcome-calendar">
      <div className="mb-3 flex items-center gap-2 border-b border-white/10 pb-3 text-slate-200">
        <CalendarDays size={18} className="text-[#8FC93A]" />
        <span className="text-sm font-bold">TFS Calendar</span>
        {loading && <span className="text-xs text-slate-400">· loading…</span>}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Main month calendar (left, uses the available width) */}
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center justify-between">
            <button data-testid="calendar-prev" onClick={() => stepMonth(-1)}
              className="rounded-lg border border-white/15 p-1.5 text-slate-200 hover:bg-white/10" aria-label="Previous month">
              <ChevronLeft size={18} />
            </button>
            <div className="text-base font-extrabold tracking-tight text-white sm:text-lg" data-testid="calendar-month-label">
              {MONTHS[sel.month]} {sel.year}
            </div>
            <button data-testid="calendar-next" onClick={() => stepMonth(1)}
              className="rounded-lg border border-white/15 p-1.5 text-slate-200 hover:bg-white/10" aria-label="Next month">
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Weekday header — 7 exactly-equal columns */}
          <div className="grid grid-cols-7 gap-[3px]">
            {DOW.map((d) => (
              <div key={d} className="pb-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-400">{d}</div>
            ))}
          </div>

          {/* Date grid — every cell identical size regardless of event count */}
          <div className="grid grid-cols-7 gap-[3px]" data-testid="calendar-grid">
            {grid.map((d, i) => {
              if (d === null) return <div key={`b${i}`} className="h-[70px] rounded-md bg-white/[0.02] sm:h-[84px]" />;
              const ds = iso(sel.year, sel.month, d);
              const items = byDate[ds] || [];
              const isToday = ds === today;
              const isSel = ds === selectedDate;
              const hasPay = items.some((e) => e.source === 'payroll');
              const show = items.length <= CAP ? items : items.slice(0, CAP - 1);
              const more = items.length <= CAP ? 0 : items.length - (CAP - 1);
              return (
                <button
                  key={ds}
                  data-testid={`calendar-day-${ds}`}
                  onClick={() => openDay(ds)}
                  style={isSel ? { boxShadow: 'inset 0 0 0 2px #0E2A47' } : (isToday ? { boxShadow: 'inset 0 0 0 2px #6FA22B' } : undefined)}
                  className="relative flex h-[70px] min-w-0 flex-col overflow-hidden rounded-md bg-[#E4EBF1] p-1 text-left transition-colors hover:bg-[#D3DEEA] sm:h-[84px]"
                >
                  <span className="flex items-center justify-between">
                    <span className={`flex h-[18px] min-w-[18px] items-center justify-center px-1 text-[11px] font-bold ${isToday ? 'rounded-full bg-[#6FA22B] text-white' : 'text-slate-700'}`}>{d}</span>
                    {hasPay && (
                      <span title="Scheduled payroll date (not proof of payment)" className="flex h-[15px] w-[15px] items-center justify-center rounded-full bg-[#1E3A8A] text-white">
                        <DollarSign size={9} strokeWidth={3} />
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 flex min-h-0 flex-1 flex-col gap-[2px] overflow-hidden">
                    {show.map((e, k) => {
                      const c = catOf(e.source);
                      return (
                        <span key={k} className="flex items-center rounded-[3px] px-1 py-[1px] text-[9px] font-semibold"
                          style={{ backgroundColor: c.color, color: c.text }}>
                          <span className="min-w-0 truncate">{e.title}</span>
                        </span>
                      );
                    })}
                    {more > 0 && (
                      <span className="px-1 text-[9px] font-bold text-slate-500" data-testid={`calendar-more-${ds}`}>+{more} more</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Small in-grid cues (today / selected / scheduled payroll) */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-400">
            <span className="inline-flex items-center gap-1"><span className="flex h-[14px] w-[14px] items-center justify-center rounded-full bg-[#6FA22B] text-[9px] font-bold text-white">1</span> Today</span>
            <span className="inline-flex items-center gap-1"><span className="h-[13px] w-[13px] rounded-[3px]" style={{ boxShadow: 'inset 0 0 0 2px #0E2A47' }} /> Selected date</span>
            <span className="inline-flex items-center gap-1"><span className="flex h-[13px] w-[13px] items-center justify-center rounded-full bg-[#2457B3] text-white"><DollarSign size={8} strokeWidth={3} /></span> Scheduled payroll date</span>
          </div>
        </div>

        {/* Miniature calendars for the other five months (narrow column, right) */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:flex lg:w-[190px] lg:shrink-0 lg:flex-col" data-testid="calendar-mini-list">
          {minis.map(({ year, month }) => {
            const cells = monthCells(year, month);
            return (
              <button
                key={`${year}-${month}`}
                data-testid={`calendar-mini-${year}${pad(month + 1)}`}
                onClick={() => setSel({ year, month })}
                className="rounded-lg border border-white/10 bg-white/[0.03] p-2 text-left transition-colors hover:border-[#8FC93A]/60 hover:bg-white/[0.06]"
              >
                <div className="mb-1 text-[11px] font-bold text-slate-200">
                  {MONTHS_SHORT[month]} <span className="text-slate-500">{year}</span>
                </div>
                <div className="grid grid-cols-7 gap-[1px]">
                  {DOW1.map((d, k) => (
                    <div key={`h${k}`} className="text-center text-[7px] font-bold uppercase text-slate-500">{d}</div>
                  ))}
                  {cells.map((d, i) => {
                    if (d === null) return <div key={`mb${i}`} className="h-[13px]" />;
                    const ds = iso(year, month, d);
                    const has = !!byDate[ds];
                    const isToday = ds === today;
                    return (
                      <div key={ds} className={`flex h-[13px] flex-col items-center justify-center text-[7.5px] leading-none ${isToday ? 'rounded-full bg-[#6FA22B] font-bold text-white' : 'text-slate-300'}`}>
                        {d}
                        {has && !isToday && <span className="mt-[1px] h-[2px] w-[2px] rounded-full bg-[#8FC93A]" />}
                      </div>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Category colour legend — compact white footer panel, plain-language names */}
      <div className="mt-5 rounded-xl bg-white px-3 py-2.5" data-testid="calendar-legend">
        <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {LEGEND_ORDER.map((k) => {
            const c = CATS[k];
            return (
              <div key={k} className="flex items-center gap-2" data-testid={`calendar-legend-${k}`}>
                <span className="h-3.5 w-3.5 shrink-0 rounded-[3px] ring-1 ring-black/10" style={{ backgroundColor: c.color }} />
                <span className="text-[11px] font-medium text-slate-700">{c.name}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day detail */}
      <Dialog open={!!detailDate} onOpenChange={(o) => !o && setDetailDate(null)}>
        <DialogContent className="max-w-md" data-testid="calendar-day-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0E2A47]">{mmddyy(detailDate)} — {detailItems.length} item{detailItems.length === 1 ? '' : 's'}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {detailItems.length === 0 && <p className="py-6 text-center text-sm text-slate-500" data-testid="calendar-detail-empty">No scheduled items on this date.</p>}
            {detailItems.map((e, i) => {
              const c = catOf(e.source);
              return (
                <div key={i} data-testid={`calendar-detail-item-${e.record_id || i}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center gap-2">
                    <span className="h-3.5 w-3.5 shrink-0 rounded-[3px] ring-1 ring-black/10" style={{ backgroundColor: c.color }} />
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{e.source_label}</span>
                    {e.status && <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">{e.status}</span>}
                  </div>
                  <div className="mt-1 text-sm font-bold text-[#0E2A47]">{e.title}</div>
                  {e.detail && <div className="text-xs text-slate-600">{e.detail}</div>}
                  {e.source === 'payroll' && <div className="mt-0.5 text-[10px] italic text-slate-500">Scheduled payroll date — not confirmation that payroll was processed or paid.</div>}
                  {e.route ? (
                    <button
                      data-testid={`calendar-detail-open-${e.record_id || i}`}
                      onClick={() => { setDetailDate(null); navigate(e.route); }}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a5f]"
                    >
                      <ExternalLink size={13} /> Open
                    </button>
                  ) : (
                    <div className="mt-1 text-[11px] italic text-slate-400" data-testid={`calendar-detail-nolink-${e.record_id || i}`}>No linked destination for this item yet.</div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WelcomeCalendar;
